import {
	Box,
	Button,
	Card,
	CardContent,
	Divider,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FormatLineSpacingOutlinedIcon from '@mui/icons-material/FormatLineSpacingOutlined';
import { useCallback, useState } from 'react';
import { useGetPreAdmissionDetailQuery } from '@/api/pre-admission/preAdmission';
import {
	FormManagementStatus,
	FormManagementType,
	FormManagementTypeEnum,
	PermissionEnum,
	PreAdmissionTab,
} from '@/api/enum/common.enum';
import FormListContainer from './FormListContainer';
import { useGetFaceSheetListQuery } from '@/api/forms-management/faceSheet';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { useGetEnquiryRecordListQuery } from '@/api/forms-management/enquiryRecord';
import { useGetAppendix4ListQuery } from '@/api/forms-management/appendix4';
import { useGetAppendix5ListQuery } from '@/api/forms-management/appendix5';
import { useGetAppendix6ListQuery } from '@/api/forms-management/appendix6';
import { useGetAppendix7ListQuery } from '@/api/forms-management/appendix7';
import { useGetNewConsentListQuery } from '@/api/forms-management/newConsent';
import { useGetMemberApplicationListQuery } from '@/api/forms-management/memberApplication';
import { useGetStopServiceListQuery } from '@/api/forms-management/stopService';
import { useGetWellBeingScaleListQuery } from '@/api/forms-management/wellBeingScale';
import { useGetRecoveryAssessmentScaleListQuery } from '@/api/forms-management/recoveryAssessmentScale';
import { useGetDsm5AdultListQuery } from '@/api/forms-management/dsm5Adult';
import { useGetDsm5TeenListQuery } from '@/api/forms-management/dsm5Teen';
import UploadFormDialog from './UploadFormDialog';
import { useGetUploadFormListQuery } from '@/api/common';
import { useGetUser } from '@/hooks/useGetUser';

export default function FormsManagementPage() {
	const [uploadType, setUploadType] = useState<FormManagementType | null>(null);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { preAdmissionId, admissionRequestId } = useParams();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [expanded, setExpanded] = useState<boolean>(false);
	const showModal = useModal();
	const [faceSheetExpanded, setFaceSheetExpanded] = useState<boolean>(false);
	const [enquiryRecordExpanded, setEnquiryRecordExpanded] =
		useState<boolean>(false);
	const [appendix4Expanded, setAppendix4Expanded] = useState<boolean>(false);
	const [appendix5Expanded, setAppendix5Expanded] = useState<boolean>(false);
	const [appendix6Expanded, setAppendix6Expanded] = useState<boolean>(false);
	const [appendix7Expanded, setAppendix7Expanded] = useState<boolean>(false);
	const [newConsentExpanded, setNewConsentExpanded] = useState<boolean>(false);
	const [stopServiceExpanded, setStopServiceExpanded] =
		useState<boolean>(false);
	const [memberApplicationExpanded, setMemberApplicationExpanded] =
		useState<boolean>(false);
	const [wellBeingScaleExpanded, setWellBeingScaleExpanded] =
		useState<boolean>(false);
	const [recoveryAssessmentScaleExpanded, setRecoveryAssessmentScaleExpanded] =
		useState<boolean>(false);
	const [dsm5AdultExpanded, setDsm5AdultExpanded] = useState<boolean>(false);
	const [dsm5TeenExpanded, setDsm5TeenExpanded] = useState<boolean>(false);
	const [intakeSummaryExpanded, setIntakeSummaryExpanded] =
		useState<boolean>(false);
	const [personCenteredCarePlanExpanded, setPersonCenteredCarePlanExpanded] =
		useState<boolean>(false);
	const [
		modifiedScaleSuicidalIdeationMSSIExpanded,
		setModifiedScaleSuicidalIdeationMSSIExpanded,
	] = useState<boolean>(false);
	const [
		twoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingExpanded,
		setTwoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingExpanded,
	] = useState<boolean>(false);
	const [
		twoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewExpanded,
		setTwoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewExpanded,
	] = useState<boolean>(false);
	const [
		otherAssessmentFormsIfNeededExpanded,
		setOtherAssessmentFormsIfNeededExpanded,
	] = useState<boolean>(false);
	const [
		caseTerminationTransferalReportExpanded,
		setCaseTerminationTransferalReportExpanded,
	] = useState<boolean>(false);
	const [caseNoteExpanded, setCaseNoteExpanded] = useState<boolean>(false);
	const [safetyPlanSuicideExpanded, setSafetyPlanSuicideExpanded] =
		useState<boolean>(false);
	const [safetyPlanViolenceExpanded, setSafetyPlanViolenceExpanded] =
		useState<boolean>(false);
	const [
		progressReportToCMOorReferrerExpanded,
		setProgressReportToCMOorReferrerExpanded,
	] = useState<boolean>(false);
	const [
		referralForCommunityPsychiatricServiceAppendix10Expanded,
		setReferralForCommunityPsychiatricServiceAppendix10Expanded,
	] = useState<boolean>(false);
	const [
		referralForWelfareServiceAppendix8Expanded,
		setReferralForWelfareServiceAppendix8Expanded,
	] = useState<boolean>(false);
	const [otherDocumentsExpanded, setOtherDocumentsExpanded] =
		useState<boolean>(false);
	const [
		WHOdisabilityAssessmentSchedule2Expanded,
		setWHOdisabilityAssessmentSchedule2Expanded,
	] = useState<boolean>(false);
	const [incidentReportSuicideExpanded, setIncidentReportSuicideExpanded] =
		useState<boolean>(false);
	const [incidentReportViolenceExpanded, setIncidentReportViolenceExpanded] =
		useState<boolean>(false);
	const [
		interRAIcommunityMentalHealth92Expanded,
		setInterRAIcommunityMentalHealth92Expanded,
	] = useState<boolean>(false);
	const { userInfo } = useGetUser();

	const { data: details }: any = useGetPreAdmissionDetailQuery(
		preAdmissionId || admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: faceSheetList }: any = useGetFaceSheetListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: enquiryRecordList }: any = useGetEnquiryRecordListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: appendix4List }: any = useGetAppendix4ListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: appendix5List }: any = useGetAppendix5ListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: appendix6List }: any = useGetAppendix6ListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: appendix7List }: any = useGetAppendix7ListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: newConsentList }: any = useGetNewConsentListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: stopServiceList }: any = useGetStopServiceListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: memberApplicationList }: any = useGetMemberApplicationListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: wellBeingScaleList }: any = useGetWellBeingScaleListQuery(
		preAdmissionId || admissionRequestId || '',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: recoveryAssessmentScaleList }: any =
		useGetRecoveryAssessmentScaleListQuery(
			preAdmissionId || admissionRequestId || '',
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: dsm5AdultList }: any = useGetDsm5AdultListQuery(
		preAdmissionId || admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: dsm5TeenList }: any = useGetDsm5TeenListQuery(
		preAdmissionId || admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: intakeSummaryList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['intake-summary'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: personCenteredCarePlanList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['person-centered-care-plan'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: modifiedScaleSuicidalIdeationMSSIList }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum[
					'the-Modified-scale-for-suicidal-deation-MSSI'
				],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const {
		data: twoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingList,
	}: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum[
				'two-minutes-risk-assessment-of-violence-applicable-for-family-member-outreaching'
			],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const {
		data: twoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewList,
	}: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum[
				'two-minutes-risk-assessment-of-violence-applicable-for-office-interview'
			],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: otherAssessmentFormsIfNeededList }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['other-assessment-forms-if-needed'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: caseTerminationTransferalReportList }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['case-termination-transferal-report'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: caseNoteList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['case-note'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: safetyPlanSuicideList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['safety-plan-suicide'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: safetyPlanViolenceList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['safety-plan-violence'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: progressReportToCMOorReferrerList }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['progress-report-to-CMO-or-referrer'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: referralForCommunityPsychiatricServiceAppendix10List }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum[
					'referral-for-community-psychiatric-service-appendix10'
				],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: referralForWelfareServiceAppendix8List }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['referral-for-welfare-service-appendix8'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: otherDocumentsList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['other-documents'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: WHOdisabilityAssessmentSchedule2List }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['WHO-disability-assessment-schedule2.0'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const { data: incidentReportSuicideList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['incident-report-suicide'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: incidentReportViolenceList }: any = useGetUploadFormListQuery(
		{
			id: preAdmissionId || admissionRequestId || '',
			type: FormManagementTypeEnum['incident-report-violence'],
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);
	const { data: interRAIcommunityMentalHealth92List }: any =
		useGetUploadFormListQuery(
			{
				id: preAdmissionId || admissionRequestId || '',
				type: FormManagementTypeEnum['interRAI-community-mental-health9.2'],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);
	const handleCreateForm = (type: string) => {
		const dataSourceMap: Record<string, any[]> = {
			[FormManagementType.faceSheet]: faceSheetList?.data || [],
			[FormManagementType.enquiryRecord]: enquiryRecordList?.data || [],
			[FormManagementType.appendix4]: appendix4List?.data || [],
			[FormManagementType.appendix5]: appendix5List?.data || [],
			[FormManagementType.appendix6]: appendix6List?.data || [],
			[FormManagementType.appendix7]: appendix7List?.data || [],
			[FormManagementType.memberApplication]: memberApplicationList?.data || [],
			[FormManagementType.newConsent]: newConsentList?.data || [],
			[FormManagementType.stopService]: stopServiceList?.data || [],
			[FormManagementType.wellBeingScale]: wellBeingScaleList?.data || [],
			[FormManagementType.recoveryAssessmentScale]:
				recoveryAssessmentScaleList?.data || [],
			[FormManagementType.dsm5Adult]: dsm5AdultList?.data || [],
			[FormManagementType.dsm5Teen]: dsm5TeenList?.data || [],
		};
		const dataList = dataSourceMap[type] || [];
		const obj = dataList.find(
			(item: any) => item.formStatus === FormManagementStatus.draft
		);

		if (obj) {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.continueFromDraftedForm')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.continueFromDraftedFormText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					otherButtonText={t('button.startNew')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						if (preAdmissionId) {
							navigate(
								`/pre-admission/pre-admission-list/detail/${
									preAdmissionId || admissionRequestId
								}/forms-management/edit-${type}/${obj.id}`
							);
						}
						if (admissionRequestId) {
							navigate(
								`/member-management/${memberListType}/detail/${
									preAdmissionId || admissionRequestId
								}/forms-management/edit-${type}/${obj.id}`
							);
						}
					}}
					onOtherConfirm={async () => {
						if (preAdmissionId) {
							navigate(
								`/pre-admission/pre-admission-list/detail/${
									preAdmissionId || admissionRequestId
								}/forms-management/create-${type}/1`
							);
						}
						if (admissionRequestId) {
							navigate(
								`/member-management/${memberListType}/detail/${
									preAdmissionId || admissionRequestId
								}/forms-management/create-${type}/1`
							);
						}
					}}
				/>
			));
		} else {
			if (preAdmissionId) {
				navigate(
					`/pre-admission/pre-admission-list/detail/${
						preAdmissionId || admissionRequestId
					}/forms-management/create-${type}/1`
				);
			}
			if (admissionRequestId) {
				navigate(
					`/member-management/${memberListType}/detail/${
						preAdmissionId || admissionRequestId
					}/forms-management/create-${type}/1`
				);
			}
		}
	};

	const createFormButton = useCallback(
		(type: FormManagementType) =>
			userInfo?.permissionList?.includes(PermissionEnum.CreateOnlineForm) && (
				<Button
					key={`create-${type}`}
					disabled={
						details?.data.preAdmissionStatus !== PreAdmissionTab.submitted &&
						type === FormManagementType.faceSheet
					}
					onClick={(e) => {
						e.stopPropagation();
						handleCreateForm(type);
					}}
					sx={{ marginRight: '8px', textTransform: 'none' }}
					variant='outlined'
				>
					{t('button.createForm')}
				</Button>
			),
		[details?.data.preAdmissionStatus, handleCreateForm, t, userInfo]
	);

	const uploadButton = useCallback(
		(type: FormManagementType) =>
			userInfo?.permissionList?.includes(PermissionEnum.UploadOnlineForm) && (
				<Button
					key={`upload-btn-${type}`}
					onClick={(e) => {
						e.stopPropagation();
						handleOpenUpload(type);
					}}
					sx={{ marginRight: '8px', textTransform: 'none' }}
					variant={type === FormManagementType.all ? 'contained' : 'outlined'}
					endIcon={
						type === FormManagementType.all ? <FileUploadOutlinedIcon /> : null
					}
				>
					{type === FormManagementType.all
						? t('button.uploadForm')
						: t('button.upload')}
				</Button>
			),
		[t, userInfo]
	);
	const handleOpenUpload = (type: FormManagementType) => {
		setUploadType(type);
	};

	const handleCloseUpload = () => {
		setUploadType(null);
	};
	const formList = [
		{
			title: t('preAdmission.faceSheet'),
			type: FormManagementType.faceSheet,
			setExpanded: setFaceSheetExpanded,
			expanded: faceSheetExpanded,
			tableList: faceSheetList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.faceSheet)}
					{uploadButton(FormManagementType.faceSheet)}
				</>
			),
		},
		{
			title: t('preAdmission.enquiryRecord'),
			type: FormManagementType.enquiryRecord,
			setExpanded: setEnquiryRecordExpanded,
			expanded: enquiryRecordExpanded,
			tableList: enquiryRecordList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.enquiryRecord)}
					{uploadButton(FormManagementType.enquiryRecord)}
				</>
			),
		},
		{
			title: t('preAdmission.appendix4ReferralForm'),
			type: FormManagementType.appendix4,
			setExpanded: setAppendix4Expanded,
			expanded: appendix4Expanded,
			tableList: appendix4List,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.appendix4)}
					{uploadButton(FormManagementType.appendix4)}
				</>
			),
		},
		{
			title: t('preAdmission.appendix5ReceiptOfReferral'),
			type: FormManagementType.appendix5,
			setExpanded: setAppendix5Expanded,
			expanded: appendix5Expanded,
			tableList: appendix5List,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.appendix5)}
					{uploadButton(FormManagementType.appendix5)}
				</>
			),
		},
		{
			title: t('preAdmission.appendix6ServiceAdmission'),
			type: FormManagementType.appendix6,
			setExpanded: setAppendix6Expanded,
			expanded: appendix6Expanded,
			tableList: appendix6List,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.appendix6)}
					{uploadButton(FormManagementType.appendix6)}
				</>
			),
		},
		{
			title: t('preAdmission.memberApplicationForm'),
			type: FormManagementType.memberApplication,
			setExpanded: setMemberApplicationExpanded,
			expanded: memberApplicationExpanded,
			tableList: memberApplicationList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.memberApplication)}
					{uploadButton(FormManagementType.memberApplication)}
				</>
			),
		},
		{
			title: t('preAdmission.newNewConsent'),
			type: FormManagementType.newConsent,
			setExpanded: setNewConsentExpanded,
			expanded: newConsentExpanded,
			tableList: newConsentList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.newConsent)}
					{uploadButton(FormManagementType.newConsent)}
				</>
			),
		},
		{
			title: t('preAdmission.stopService'),
			type: FormManagementType.stopService,
			setExpanded: setStopServiceExpanded,
			expanded: stopServiceExpanded,
			tableList: stopServiceList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.stopService)}
					{uploadButton(FormManagementType.stopService)}
				</>
			),
		},
		{
			title: t('preAdmission.appendix7ServiceAdmission'),
			type: FormManagementType.appendix7,
			setExpanded: setAppendix7Expanded,
			expanded: appendix7Expanded,
			tableList: appendix7List,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.appendix7)}
					{uploadButton(FormManagementType.appendix7)}
				</>
			),
		},
		{
			title: t('preAdmission.intakeSummary'),
			type: FormManagementType.intakeSummary,
			setExpanded: setIntakeSummaryExpanded,
			expanded: intakeSummaryExpanded,
			tableList: intakeSummaryList,
			buttonElement: <>{uploadButton(FormManagementType.intakeSummary)}</>,
		},
		{
			title: t('preAdmission.wellBeingScale'),
			type: FormManagementType.wellBeingScale,
			setExpanded: setWellBeingScaleExpanded,
			expanded: wellBeingScaleExpanded,
			tableList: wellBeingScaleList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.wellBeingScale)}
					{uploadButton(FormManagementType.wellBeingScale)}
				</>
			),
		},
		{
			title: t('preAdmission.recoveryAssessmentScale'),
			type: FormManagementType.recoveryAssessmentScale,
			setExpanded: setRecoveryAssessmentScaleExpanded,
			expanded: recoveryAssessmentScaleExpanded,
			tableList: recoveryAssessmentScaleList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.recoveryAssessmentScale)}
					{uploadButton(FormManagementType.recoveryAssessmentScale)}
				</>
			),
		},
		{
			title: t('preAdmission.personCenteredCarePlan'),
			type: FormManagementType.personCenteredCarePlan,
			setExpanded: setPersonCenteredCarePlanExpanded,
			expanded: personCenteredCarePlanExpanded,
			tableList: personCenteredCarePlanList,
			buttonElement: (
				<>{uploadButton(FormManagementType.personCenteredCarePlan)}</>
			),
		},
		{
			title: t('preAdmission.dsm5Adult'),
			type: FormManagementType.dsm5Adult,
			setExpanded: setDsm5AdultExpanded,
			expanded: dsm5AdultExpanded,
			tableList: dsm5AdultList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.dsm5Adult)}
					{uploadButton(FormManagementType.dsm5Adult)}
				</>
			),
		},
		{
			title: t('preAdmission.dsm5Teen'),
			type: FormManagementType.dsm5Teen,
			setExpanded: setDsm5TeenExpanded,
			expanded: dsm5TeenExpanded,
			tableList: dsm5TeenList,
			buttonElement: (
				<>
					{createFormButton(FormManagementType.dsm5Teen)}
					{uploadButton(FormManagementType.dsm5Teen)}
				</>
			),
		},
		{
			title: t('preAdmission.modifiedScaleSuicidalIdeationMSSI'),
			type: FormManagementType.theModifiedScaleForSuicidalDeationMssi,
			setExpanded: setModifiedScaleSuicidalIdeationMSSIExpanded,
			expanded: modifiedScaleSuicidalIdeationMSSIExpanded,
			tableList: modifiedScaleSuicidalIdeationMSSIList,
			buttonElement: (
				<>
					{uploadButton(
						FormManagementType.theModifiedScaleForSuicidalDeationMssi
					)}
				</>
			),
		},
		{
			title: t(
				'preAdmission.twoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreaching'
			),
			type: FormManagementType.twoMinutesRiskAssessmentOfViolenceApplicableForFamilyMemberOutreaching,
			setExpanded:
				setTwoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingExpanded,
			expanded:
				twoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingExpanded,
			tableList:
				twoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingList,
			buttonElement: (
				<>
					{uploadButton(
						FormManagementType.twoMinutesRiskAssessmentOfViolenceApplicableForFamilyMemberOutreaching
					)}
				</>
			),
		},
		{
			title: t(
				'preAdmission.twoMinutesRiskAssessmentViolenceApplicableForOfficeInterview'
			),
			type: FormManagementType.twoMinutesRiskAssessmentOfViolenceApplicableForOfficeInterview,
			setExpanded:
				setTwoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewExpanded,
			expanded:
				twoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewExpanded,
			tableList:
				twoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewList,
			buttonElement: (
				<>
					{uploadButton(
						FormManagementType.twoMinutesRiskAssessmentOfViolenceApplicableForOfficeInterview
					)}
				</>
			),
		},
		{
			title: t('preAdmission.otherAssessmentFormsIfNeeded'),
			type: FormManagementType.otherAssessmentFormsIfNeeded,
			setExpanded: setOtherAssessmentFormsIfNeededExpanded,
			expanded: otherAssessmentFormsIfNeededExpanded,
			tableList: otherAssessmentFormsIfNeededList,
			buttonElement: (
				<>{uploadButton(FormManagementType.otherAssessmentFormsIfNeeded)}</>
			),
		},
		{
			title: t('preAdmission.caseTerminationTransferalReport'),
			type: FormManagementType.caseTerminationTransferalReport,
			setExpanded: setCaseTerminationTransferalReportExpanded,
			expanded: caseTerminationTransferalReportExpanded,
			tableList: caseTerminationTransferalReportList,
			buttonElement: (
				<>{uploadButton(FormManagementType.caseTerminationTransferalReport)}</>
			),
		},
		{
			title: t('case.caseNote'),
			type: FormManagementType.caseNote,
			setExpanded: setCaseNoteExpanded,
			expanded: caseNoteExpanded,
			tableList: caseNoteList,
			buttonElement: <>{uploadButton(FormManagementType.caseNote)}</>,
		},
		{
			title: t('preAdmission.safetyPlanSuicide'),
			type: FormManagementType.safetyPlanSuicide,
			setExpanded: setSafetyPlanSuicideExpanded,
			expanded: safetyPlanSuicideExpanded,
			tableList: safetyPlanSuicideList,
			buttonElement: <>{uploadButton(FormManagementType.safetyPlanSuicide)}</>,
		},
		{
			title: t('preAdmission.safetyPlanViolence'),
			type: FormManagementType.safetyPlanViolence,
			setExpanded: setSafetyPlanViolenceExpanded,
			expanded: safetyPlanViolenceExpanded,
			tableList: safetyPlanViolenceList,
			buttonElement: <>{uploadButton(FormManagementType.safetyPlanViolence)}</>,
		},
		{
			title: t('preAdmission.progressReportToCMOorReferrer'),
			type: FormManagementType.progressReportToCmoOrReferrer,
			setExpanded: setProgressReportToCMOorReferrerExpanded,
			expanded: progressReportToCMOorReferrerExpanded,
			tableList: progressReportToCMOorReferrerList,
			buttonElement: (
				<>{uploadButton(FormManagementType.progressReportToCmoOrReferrer)}</>
			),
		},
		{
			title: t('preAdmission.referralForCommunityPsychiatricServiceAppendix10'),
			type: FormManagementType.referralForCommunityPsychiatricServiceAppendix10,
			setExpanded: setReferralForCommunityPsychiatricServiceAppendix10Expanded,
			expanded: referralForCommunityPsychiatricServiceAppendix10Expanded,
			tableList: referralForCommunityPsychiatricServiceAppendix10List,
			buttonElement: (
				<>
					{uploadButton(
						FormManagementType.referralForCommunityPsychiatricServiceAppendix10
					)}
				</>
			),
		},
		{
			title: t('preAdmission.referralForWelfareServiceAppendix8'),
			type: FormManagementType.referralForWelfareServiceAppendix8,
			setExpanded: setReferralForWelfareServiceAppendix8Expanded,
			expanded: referralForWelfareServiceAppendix8Expanded,
			tableList: referralForWelfareServiceAppendix8List,
			buttonElement: (
				<>
					{uploadButton(FormManagementType.referralForWelfareServiceAppendix8)}
				</>
			),
		},
		{
			title: t('preAdmission.otherDocuments'),
			type: FormManagementType.otherDocuments,
			setExpanded: setOtherDocumentsExpanded,
			expanded: otherDocumentsExpanded,
			tableList: otherDocumentsList,
			buttonElement: <>{uploadButton(FormManagementType.otherDocuments)}</>,
		},
		{
			title: t('preAdmission.WHOdisabilityAssessmentSchedule2'),
			type: FormManagementType.whoDisabilityAssessmentSchedule2_0,
			setExpanded: setWHOdisabilityAssessmentSchedule2Expanded,
			expanded: WHOdisabilityAssessmentSchedule2Expanded,
			tableList: WHOdisabilityAssessmentSchedule2List,
			buttonElement: (
				<>
					{uploadButton(FormManagementType.whoDisabilityAssessmentSchedule2_0)}
				</>
			),
		},
		{
			title: t('preAdmission.incidentReportSuicide'),
			type: FormManagementType.incidentReportSuicide,
			setExpanded: setIncidentReportSuicideExpanded,
			expanded: incidentReportSuicideExpanded,
			tableList: incidentReportSuicideList,
			buttonElement: (
				<>{uploadButton(FormManagementType.incidentReportSuicide)}</>
			),
		},
		{
			title: t('preAdmission.incidentReportViolence'),
			type: FormManagementType.incidentReportViolence,
			setExpanded: setIncidentReportViolenceExpanded,
			expanded: incidentReportViolenceExpanded,
			tableList: incidentReportViolenceList,
			buttonElement: (
				<>{uploadButton(FormManagementType.incidentReportViolence)}</>
			),
		},
		{
			title: t('preAdmission.interRAIcommunityMentalHealth9.2'),
			type: FormManagementType.interRaiCommunityMentalHealth9_2,
			setExpanded: setInterRAIcommunityMentalHealth92Expanded,
			expanded: interRAIcommunityMentalHealth92Expanded,
			tableList: interRAIcommunityMentalHealth92List,
			buttonElement: (
				<>{uploadButton(FormManagementType.interRaiCommunityMentalHealth9_2)}</>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between' marginBottom={3}>
					<Typography variant='h4'>
						{t('drawerListItems.formsManagement')}
					</Typography>
				</Box>

				<Card>
					<CardContent sx={{ padding: '24px' }}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Box
							display='flex'
							justifyContent='flex-end'
							marginTop={'24px'}
							marginBottom={3}
						>
							<Button
								endIcon={<FormatLineSpacingOutlinedIcon />}
								onClick={() => {
									setFaceSheetExpanded(!expanded);
									setEnquiryRecordExpanded(!expanded);
									setAppendix4Expanded(!expanded);
									setAppendix5Expanded(!expanded);
									setAppendix6Expanded(!expanded);
									setAppendix7Expanded(!expanded);
									setMemberApplicationExpanded(!expanded);
									setNewConsentExpanded(!expanded);
									setStopServiceExpanded(!expanded);
									setWellBeingScaleExpanded(!expanded);
									setRecoveryAssessmentScaleExpanded(!expanded);
									setDsm5AdultExpanded(!expanded);
									setDsm5TeenExpanded(!expanded);
									setIntakeSummaryExpanded(!expanded);
									setModifiedScaleSuicidalIdeationMSSIExpanded(!expanded);
									setTwoMinutesRiskAssessmentViolenceApplicableFamilyMemberOutreachingExpanded(
										!expanded
									);
									setTwoMinutesRiskAssessmentViolenceApplicableForOfficeInterviewExpanded(
										!expanded
									);
									setOtherAssessmentFormsIfNeededExpanded(!expanded);
									setCaseTerminationTransferalReportExpanded(!expanded);
									setCaseNoteExpanded(!expanded);
									setSafetyPlanSuicideExpanded(!expanded);
									setSafetyPlanViolenceExpanded(!expanded);
									setProgressReportToCMOorReferrerExpanded(!expanded);
									setReferralForCommunityPsychiatricServiceAppendix10Expanded(
										!expanded
									);
									setReferralForWelfareServiceAppendix8Expanded(!expanded);
									setOtherDocumentsExpanded(!expanded);
									setWHOdisabilityAssessmentSchedule2Expanded(!expanded);
									setIncidentReportSuicideExpanded(!expanded);
									setIncidentReportViolenceExpanded(!expanded);
									setInterRAIcommunityMentalHealth92Expanded(!expanded);
									setExpanded(!expanded);
								}}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('button.expandCollapseAll')}
							</Button>
							{/* <UploadFormDialog /> */}
							{uploadButton(FormManagementType.all)}
							{uploadType && (
								<UploadFormDialog
									open={!!uploadType}
									onClose={handleCloseUpload}
									type={uploadType}
								/>
							)}
						</Box>
						{formList.map((item: any) => (
							<FormListContainer
								key={item.type}
								title={item.title}
								setExpanded={item.setExpanded}
								tableList={item.tableList}
								expanded={item.expanded}
								type={item.type}
								buttonElement={item.buttonElement}
							/>
						))}
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
